import { z } from 'zod';

export const Platform = z.enum(['twitch', 'youtube', 'kick', 'hypevault']);
export type Platform = z.infer<typeof Platform>;

const EventBase = z.object({
  id: z.string().uuid(),
  streamerId: z.string().uuid(),
  timestamp: z.number().int(),
  platform: Platform,
});

export const TipEvent = EventBase.extend({
  type: z.literal('tip'),
  viewerId: z.string().uuid().nullable(),
  viewerName: z.string(),
  coins: z.number().int().positive(),
  message: z.string().nullable(),
});

/**
 * Real-money tip received via Stripe Connect. Replaces the legacy coin-based
 * TipEvent for everything except historical stream_events rows. Widgets that
 * already handled `type: 'tip'` should also accept this — see goal / ticker
 * / leaderboard / alert widgets.
 */
export const TipReceivedEvent = EventBase.extend({
  type: z.literal('tip_received'),
  viewerId: z.string().uuid().nullable(),
  viewerName: z.string(),
  amountCents: z.number().int().positive(),
  message: z.string().nullable(),
});

export const FollowEvent = EventBase.extend({
  type: z.literal('follow'),
  viewerId: z.string().nullable(),
  viewerName: z.string(),
});

export const SubEvent = EventBase.extend({
  type: z.literal('sub'),
  viewerId: z.string().nullable(),
  viewerName: z.string(),
  tier: z.enum(['prime', 't1', 't2', 't3', 'yt-member', 'kick-sub', 'hypevault']),
  months: z.number().int().nonnegative(),
  isGift: z.boolean(),
  gifterName: z.string().nullable(),
});

export const CheerEvent = EventBase.extend({
  type: z.literal('cheer'),
  /** Nullable to mirror Twitch's `is_anonymous` cheers, which arrive
   *  without a user_id and only carry the placeholder "anonymous" name. */
  viewerId: z.string().nullable().optional(),
  viewerName: z.string(),
  bits: z.number().int().positive(),
  message: z.string().nullable(),
});

export const RaidEvent = EventBase.extend({
  type: z.literal('raid'),
  raiderName: z.string(),
  viewers: z.number().int().nonnegative(),
});

export const AlertPurchaseEvent = EventBase.extend({
  type: z.literal('alertPurchase'),
  viewerId: z.string().uuid(),
  viewerName: z.string(),
  productId: z.string().uuid(),
  productName: z.string(),
  coins: z.number().int().positive(),
});

export const CardRedemptionEvent = EventBase.extend({
  type: z.literal('cardRedemption'),
  viewerId: z.string().uuid(),
  viewerName: z.string(),
  cardId: z.string().uuid(),
  cardName: z.string(),
  cardImageUrl: z.string().nullish(),
  cardRarity: z.enum(['common', 'rare', 'epic', 'legendary', 'mythic']).nullish(),
  cardDescription: z.string().nullish(),
  cardMessage: z.string().nullish(),
  // 'message' / 'tts' / 'sound' kept on the EVENT side for any historic
  // payloads still in `stream_events` from before migration 0081 — the
  // current cards.action_kind column no longer mints them.
  action: z.enum([
    'message',
    'tts',
    'sound',
    'video',
    'custom',
    'chaos',
    'jumpscare',
    'timer',
    'none',
  ]),
  /** Per-card marathon time grant. 0/omitted = use streamer's default
   *  hypevault_card_redemption value from the marathon timeMap. */
  cardMarathonSeconds: z.number().int().nonnegative().optional(),
  /** Set true when this event represents a viewer purchase of cards
   *  (Stripe card-shop checkout or — legacy — a gem pack open). Routes
   *  the marathon time grant through `hypevault_card_purchase` instead
   *  of `hypevault_card_redemption` so streamers can value a purchase
   *  differently from a played card. */
  isPurchase: z.boolean().optional(),
  /** @deprecated alias for `isPurchase` — kept so any in-flight
   *  envelopes published before the rename still route correctly. */
  isPack: z.boolean().optional(),
  /** Universal add-ons — all three apply additively to whatever the primary
   *  action does. Set on the card itself, server-side. Widgets render these
   *  per their own capability (CardAlert plays soundUrl + speaks ttsMessage;
   *  chatMessage is dispatched server-side to the streamer's chat platforms). */
  cardChatMessage: z.string().nullish(),
  cardTtsMessage: z.string().nullish(),
  cardSoundUrl: z.string().nullish(),
});

export const SuperChatEvent = EventBase.extend({
  type: z.literal('superChat'),
  viewerId: z.string().nullable(),
  viewerName: z.string(),
  amountCents: z.number().int().nonnegative(),
  currency: z.string().default('USD'),
  message: z.string().nullable(),
});

export const SuperStickerEvent = EventBase.extend({
  type: z.literal('superSticker'),
  viewerId: z.string().nullable(),
  viewerName: z.string(),
  amountCents: z.number().int().nonnegative(),
  currency: z.string().default('USD'),
  stickerId: z.string().nullable(),
});

export const ChannelPointRedemptionEvent = EventBase.extend({
  type: z.literal('channelPointRedemption'),
  viewerId: z.string().nullable(),
  viewerName: z.string(),
  rewardId: z.string(),
  rewardTitle: z.string(),
  rewardCost: z.number().int().nonnegative(),
  /** Viewer-supplied input (only present for rewards that require text). */
  userInput: z.string().nullable(),
});

export const StreamEvent = z.discriminatedUnion('type', [
  TipEvent,
  TipReceivedEvent,
  FollowEvent,
  SubEvent,
  CheerEvent,
  RaidEvent,
  AlertPurchaseEvent,
  CardRedemptionEvent,
  SuperChatEvent,
  SuperStickerEvent,
  ChannelPointRedemptionEvent,
]);

export type StreamEvent = z.infer<typeof StreamEvent>;
export type TipEvent = z.infer<typeof TipEvent>;
export type TipReceivedEvent = z.infer<typeof TipReceivedEvent>;
export type FollowEvent = z.infer<typeof FollowEvent>;
export type SubEvent = z.infer<typeof SubEvent>;
export type CheerEvent = z.infer<typeof CheerEvent>;
export type RaidEvent = z.infer<typeof RaidEvent>;
export type AlertPurchaseEvent = z.infer<typeof AlertPurchaseEvent>;
export type CardRedemptionEvent = z.infer<typeof CardRedemptionEvent>;
export type SuperChatEvent = z.infer<typeof SuperChatEvent>;
export type SuperStickerEvent = z.infer<typeof SuperStickerEvent>;
export type ChannelPointRedemptionEvent = z.infer<typeof ChannelPointRedemptionEvent>;
